<template>
  <div>
    <v-row justify="center" no-gutters>
      <v-col cols="12" lg="8">
        <new-perseu-card title="Terapias realizadas">
          <template #actions>
            <!-- 🔥 Indicador de polling ativo -->
            <v-chip
              v-if="hasTherapiesInQueue"
              small
              color="info"
              text-color="white"
              class="mr-2"
            >
              <v-icon small left class="rotating-icon">mdi-sync</v-icon>
              Atualizando...
            </v-chip>

            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  small
                  :disabled="!canExportBatch"
                  v-bind="attrs"
                  class="ml-2"
                  v-on="on"
                >
                  <v-icon small class="mr-1">download</v-icon>
                  Exportar terapias finalizadas
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="exportAllTherapistReports">
                  <v-list-item-title>
                    <v-icon small class="mr-2">description</v-icon>
                    Visão do Terapeuta
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportAllPatientReports">
                  <v-list-item-title>
                    <v-icon small class="mr-2">assignment</v-icon>
                    Visão do Paciente
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <v-row class="px-6 py-6">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Pesquisar por nome, email ou CPF (apenas números) do paciente"
              single-line
              hide-details
            ></v-text-field>
          </v-row>

          <v-list v-if="hasTherapiesToShow" three-line>
            <v-list-item
              v-for="(therapy, index) in currentTherapiesList"
              :key="index"
              class="border-bottom-grey"
              @click="showActions({ item: therapy, index })"
            >
              <v-list-item-avatar>
                <tooltip-icon
                  v-show="isTherapyStatusSynced(therapy)"
                  color="primary"
                  icon="cloud_sync"
                  text="Processado"
                  large
                />
                <tooltip-icon
                  v-show="isTherapyStatusNotSynced(therapy)"
                  color="warning"
                  icon="sync_disabled"
                  text="Não processado"
                  large
                />
                <tooltip-icon
                  v-show="isTherapyStatusInQueue(therapy)"
                  color="info"
                  icon="playlist_add_check"
                  text="Na fila para processamento"
                  large
                />
                <tooltip-icon
                  v-show="isTherapyStatusInQueueWithError(therapy)"
                  color="error"
                  icon="sync_problem"
                  text="Erro ao realizar processamento"
                  large
                />
                <tooltip-icon
                  v-show="isTherapyStatusInProgress(therapy)"
                  color="warning"
                  icon="pending"
                  text="Não finalizado"
                  large
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>
                  {{ therapy.patientName }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ `Email: ${therapy.email}` }}
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  {{ `Documento: ${therapy.document}` }}
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  Data da terapia:
                  {{ therapy.realizedAt | formatDateTime }}
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  Status:
                  <span v-if="therapy.protocol"> Terapia Finalizada </span>
                  <div v-else>
                    <span v-if="isTherapyStatusInQueue(therapy)"
                      >Em fila de processamento</span
                    >
                    <div v-else>
                      <span v-if="!isTherapyStatusInProgress(therapy)"
                        >Terapia finalizada/não sincronizada</span
                      >
                      <span v-if="isTherapyStatusInProgress(therapy)"
                        >Terapia não finalizada/não sincronizada</span
                      >
                    </div>
                  </div>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon class="mt-3">open_in_new</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <template v-else>
            <empty-list text="Nenhuma terapia realizada ainda." />
          </template>
          <div v-if="!currentTherapiesList.length && search">
            <empty-list text="Nenhum resultado para a pesquisa." />
          </div>
        </new-perseu-card>
      </v-col>
    </v-row>
    <v-bottom-sheet v-model="dialogActions">
      <v-sheet>
        <div class="pa-3">
          <span class="title">Ações</span>
        </div>
        <v-divider></v-divider>
        <v-list class="text-center">
          <!-- 🔥 CONTINUAR TERAPIA - Apenas se IN_PROGRESS -->
          <template v-if="isSelectedTherapyInProgress">
            <v-list-item @click="resume">
              <v-list-item-title>
                <v-icon>redo</v-icon>
                Continuar terapia
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
          </template>

          <!-- 🔥 SINCRONIZAR - Apenas se NOT_SYNCED (finalizada mas offline) -->
          <template v-if="isSelectedTherapyNotSynced">
            <v-list-item @click="syncTherapy">
              <v-list-item-title>
                <v-icon>sync</v-icon>
                Sincronizar com servidor
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
          </template>

          <!-- 🔥 VER DIAGNÓSTICO - Apenas se SYNCED -->
          <template v-if="isSelectedTherapySynced">
            <v-list-item @click="viewDiagnosis">
              <v-list-item-title>
                <v-icon>pageview</v-icon>
                Ver diagnóstico
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
          </template>

          <!-- 🔥 SALVAR BACKUP LOCAL - Apenas se NOT_SYNCED ou IN_PROGRESS -->
          <template
            v-if="isSelectedTherapyNotSynced || isSelectedTherapyInProgress"
          >
            <v-list-item @click="saveLocalyAsJson">
              <v-list-item-title>
                <v-icon>save</v-icon>
                Salvar backup localmente
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
          </template>

          <!-- 🔥 EXCLUIR - Sempre disponível -->
          <v-list-item @click="confirmDeleteTherapy">
            <v-list-item-title>
              <v-icon>delete_forever</v-icon>
              Excluir terapia
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-sheet>
    </v-bottom-sheet>
    <fixed-button text="Nova terapia" icon="note_add" @click="createTherapy" />
  </div>
</template>

<script>
import {
  findByTherapist,
  downloadTherapistBatchPdf,
  downloadPatientBatchPdf,
} from "@/services/external/therapies-service";
import { TherapyStatusEnum } from "@/domains/therapy/shared/application/therapy-status.enum";
import _ from "lodash";
import {
  listDrafts,
  getDraftContent,
} from "@/services/external/therapy-draft-service";

export default {
  components: {},
  data: () => ({
    therapies: [],
    filteredTherapies: [],
    headers: [
      { text: "Número", value: "id" },
      { text: "Paciente", value: "patient.name" },
      { text: "Data", value: "createdAt", width: "150" },
      { value: "synced", width: "150", sortable: false },
      { value: "finished", width: "150", sortable: false },
      {
        text: "Ações",
        value: "actions",
        sortable: false,
        width: "150",
      },
    ],
    dialogDiagnosis: false,
    therapy: {},
    dialogActions: false,
    selectedItem: {
      index: "",
    },
    search: "",
    debouncedFilter: _.debounce(function () {
      this.filterTherapies();
    }, 400),
    pollingTimer: null, // Timer para polling de terapias em fila
    pollingInterval: 15000, // 15 segundos
    deletedDraftIds: new Set(), // 🔥 Cache de IDs de drafts deletados (evita reaparecimento no polling)
  }),
  computed: {
    isSelectedTherapySynced() {
      return this.selectedItem.status === TherapyStatusEnum.SYNCED;
    },
    isSelectedTherapyInProgress() {
      return this.selectedItem.status === TherapyStatusEnum.IN_PROGRESS;
    },
    isSelectedTherapyNotSynced() {
      return this.selectedItem.status === TherapyStatusEnum.NOT_SYNCED;
    },
    hasTherapies() {
      return this.therapies && this.therapies.length > 0;
    },
    hasTherapiesToShow() {
      return this.hasTherapies;
    },
    currentTherapiesList() {
      return this.filteredTherapies;
    },
    hasSyncedTherapies() {
      const syncedTherapies = this.currentTherapiesList.filter(
        (t) => t.status === TherapyStatusEnum.SYNCED && t.protocol
      );
      console.log(
        "hasSyncedTherapies - Total de terapias:",
        this.currentTherapiesList.length
      );
      console.log(
        "hasSyncedTherapies - Terapias sincronizadas com protocolo:",
        syncedTherapies.length
      );
      console.log(
        "hasSyncedTherapies - TherapyStatusEnum.SYNCED:",
        TherapyStatusEnum.SYNCED
      );
      if (this.currentTherapiesList.length > 0) {
        console.log(
          "hasSyncedTherapies - Exemplo de terapia:",
          this.currentTherapiesList[0]
        );
      }
      return syncedTherapies.length > 0;
    },
    canExportBatch() {
      // Pode exportar se houver pelo menos uma terapia sincronizada com protocolo
      return this.hasSyncedTherapies;
    },
    syncedProtocols() {
      return this.currentTherapiesList
        .filter((t) => t.status === TherapyStatusEnum.SYNCED && t.protocol)
        .map((t) => t.protocol);
    },
    // 🔥 Verifica se há terapias em fila de processamento
    hasTherapiesInQueue() {
      return this.currentTherapiesList.some(
        (t) =>
          t.status === TherapyStatusEnum.IN_QUEUE ||
          t.status === TherapyStatusEnum.IN_QUEUE_WITH_ERROR
      );
    },
  },
  watch: {
    search() {
      this.debouncedFilter();
    },
    // 🔥 Inicia/para polling baseado na presença de terapias em fila
    hasTherapiesInQueue: {
      handler(hasInQueue) {
        if (hasInQueue) {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      },
      immediate: false,
    },
  },
  created() {
    this.find();
  },
  beforeDestroy() {
    // Limpa o polling ao sair da página
    this.stopPolling();
  },
  methods: {
    filterTherapies() {
      if (!this.search) {
        this.filteredTherapies = this.therapies
          .slice()
          .sort(
            (a, b) =>
              new Date(b.realizedAt).getTime() -
              new Date(a.realizedAt).getTime()
          );
        return;
      }
      const searchLower = this.search.toLowerCase().trim();

      this.filteredTherapies = this.therapies
        .filter((therapy) => {
          let find = false;

          find = therapy.patientName
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .includes(searchLower);

          if (find) return true;

          find =
            therapy.email && therapy.email.toLowerCase().includes(searchLower);

          if (find) return true;

          const searchNumbersOnly = this.search.replace(/\D/g, "");

          if (searchNumbersOnly.length === 0) return false;

          return (
            therapy.document && therapy.document.includes(searchNumbersOnly)
          );
        })
        .sort(
          (a, b) =>
            new Date(b.realizedAt).getTime() - new Date(a.realizedAt).getTime()
        );
    },
    createTherapy() {
      this.$store.dispatch("therapy/newTherapy");
    },
    showActions({ index, item }) {
      this.selectedItem = {
        index,
        ...item,
      };
      this.dialogActions = true;
    },
    async resume() {
      try {
        // Se é um draft da nuvem, busca conteúdo completo do S3
        if (this.selectedItem.therapy.isCloudDraft) {
          this.$store.dispatch("loading/openDialog");

          console.log(
            "[Resume] Recuperando draft da nuvem:",
            this.selectedItem.therapy.draftId
          );

          const therapyData = await getDraftContent(
            this.selectedItem.therapy.draftId
          );

          // 🔍 DEBUG: Verifica estrutura do backup S3
          console.log("[Resume] 📦 Conteúdo do backup S3:", therapyData);
          console.log(
            "[Resume] 📦 Campo question.historicoDeDoencaAtual:",
            therapyData.question?.historicoDeDoencaAtual
          );

          await this.$store.dispatch("therapy/loadTherapy", therapyData);

          // Fecha o loading antes de exibir modal
          this.$store.dispatch("loading/closeDialog");

          // Mensagem amigável em modal
          await this.$swal({
            title: "Terapia recuperada",
            html:
              "Encontramos seus dados salvos e recomeçamos do início para garantir tudo certinho.<br/>" +
              "Você pode avançar até onde parou.",
            icon: "info",
            confirmButtonText: "Continuar",
            confirmButtonColor: "#1976D2",
          });
        } else {
          // Draft local ou terapia em progresso normal
          this.$store.dispatch(
            "therapy/loadTherapy",
            this.selectedItem.therapy
          );
        }

        this.$router.push({ name: "therapys.stepper" });
      } catch (error) {
        console.error("[Resume] Erro ao recuperar terapia:", error);
        this.$toasted.error("Erro ao recuperar terapia. Tente novamente.");
        this.$store.dispatch("loading/closeDialog");
      }
    },
    async find() {
      try {
        this.$store.dispatch("loading/openDialog");

        // 🔥 0. Limpa terapias órfãs do IndexedDB (com draftId) antes de buscar
        if (navigator.onLine) {
          try {
            const { cleanupOrphanDrafts } = await import(
              "@/services/local/therapies-service"
            );
            const cleaned = await cleanupOrphanDrafts();
            if (cleaned > 0) {
              console.log(
                `[Find] 🧹 ${cleaned} terapia(s) órfã(s) removida(s) do IndexedDB`
              );
            }
          } catch (error) {
            console.error("[Find] Erro ao limpar terapias órfãs:", error);
          }
        }

        // 1. Busca terapias do backend e locais
        this.therapies = await findByTherapist();

        // 2. Busca drafts da nuvem (S3) se estiver online
        if (navigator.onLine) {
          try {
            let cloudDrafts = await listDrafts();

            // 🔥 FIX: Filtra drafts deletados (evita reaparecimento após reload)
            cloudDrafts = cloudDrafts.filter((draft) => {
              const wasDeleted = this.deletedDraftIds.has(draft.id);
              if (wasDeleted) {
                console.log(
                  "[Find] ⏭️ Ignorando draft deletado (ID:",
                  draft.id,
                  ")"
                );
              }
              return !wasDeleted;
            });

            // Formata drafts da nuvem como terapias em progresso
            const formattedDrafts = cloudDrafts.map((draft) => {
              return {
                patientName:
                  draft.patient?.name ||
                  draft.patientName ||
                  draft.protocol ||
                  "Rascunho sem nome",
                email: draft.patient?.mail || draft.patientEmail || "",
                document: draft.patient?.userData?.document || "",
                realizedAt: draft.createdAt,
                status: TherapyStatusEnum.IN_PROGRESS,
                therapy: {
                  ...draft,
                  status: TherapyStatusEnum.IN_PROGRESS,
                  draftId: draft.id,
                  protocol: draft.protocol,
                  presignedUrl: draft.presignedUrl,
                  presignedUrlExpiresAt: draft.presignedUrlExpiresAt,
                  s3BackupKey: draft.s3BackupKey,
                  step: draft.currentStep,
                  isCloudDraft: true, // Flag para identificar que veio da nuvem
                  patient: {
                    id: draft.patient?.id || null,
                    name: draft.patient?.name || draft.patientName || "",
                    email: draft.patient?.mail || draft.patientEmail || "",
                    document: draft.patient?.userData?.document || "",
                  },
                },
              };
            });

            // Adiciona drafts da nuvem à lista
            this.therapies = [...formattedDrafts, ...this.therapies];

            // 🛡️ Remove duplicatas baseadas no protocolo (caso terapia esteja tanto no IndexedDB quanto no S3)
            const uniqueTherapies = new Map();
            this.therapies.forEach((therapy) => {
              const key =
                therapy.therapy?.protocol ||
                therapy.protocol ||
                `local_${therapy.therapy?.localId || Math.random()}`;
              // Prioriza drafts da nuvem (isCloudDraft) sobre locais
              if (!uniqueTherapies.has(key) || therapy.therapy?.isCloudDraft) {
                uniqueTherapies.set(key, therapy);
              }
            });
            this.therapies = Array.from(uniqueTherapies.values());

            console.log(
              `[Therapies List] ${cloudDrafts.length} draft(s) da nuvem carregado(s)`
            );
          } catch (error) {
            console.error(
              "[Therapies List] Erro ao buscar drafts da nuvem:",
              error
            );
            // Não bloqueia o carregamento das terapias locais
          }
        }

        this.filterTherapies();
      } catch (error) {
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },
    viewDiagnosis() {
      this.dialogActions = false;
      const { protocol } = this.selectedItem;
      this.$router.push({ name: "therapistReview", query: { protocol } });
    },
    async syncTherapy() {
      try {
        this.$store.dispatch("loading/openDialog");
        const { index, therapy } = this.selectedItem;
        const newItem = await this.$store.dispatch(
          "therapy/syncOneTherapy",
          therapy
        );
        this.therapies.splice(index, 1);
        this.therapies.unshift(newItem);
        this.dialogActions = false;
        this.$toasted.global.defaultSuccess();
      } catch (error) {
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },

    async saveLocalyAsJson() {
      try {
        this.$store.dispatch("loading/openDialog");
        const { therapy } = this.selectedItem;
        await this.$store.dispatch("therapy/saveAsJsonLocally", therapy);
        this.$toasted.global.defaultSuccess();
      } catch (error) {
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },
    confirmDeleteTherapy() {
      this.dialogActions = false;

      const isSynced = this.isSelectedTherapySynced;
      const title = "Confirmar exclusão";
      const message = isSynced
        ? "Tem certeza que deseja excluir esta terapia? Esta ação não poderá ser desfeita."
        : "Tem certeza que deseja excluir esta terapia? Esta ação não poderá ser desfeita.";

      this.$swal({
        title,
        text: message,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#FF5252",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteTherapy();
        }
      });
    },
    async deleteTherapy() {
      try {
        this.$store.dispatch("loading/openDialog");

        // 🔥 Adiciona ao cache de deletados ANTES de deletar (para prevenir reaparecimento no polling)
        if (this.selectedItem.therapy?.draftId) {
          this.deletedDraftIds.add(this.selectedItem.therapy.draftId);
          console.log(
            "[Delete] Draft ID adicionado ao cache de deletados:",
            this.selectedItem.therapy.draftId
          );
        }

        await this.$store.dispatch("therapy/deleteTherapy", {
          index: this.selectedItem.index,
          therapy: this.selectedItem.therapy,
          status: this.selectedItem.status,
          protocol: this.selectedItem.protocol,
        });
        this.$toasted.global.defaultSuccess({
          message: "Terapia excluída com sucesso!",
        });
        this.therapies.splice(this.selectedItem.index, 1);
        this.filterTherapies();
      } catch (error) {
        // 🔥 Se falhar, remove do cache de deletados
        if (this.selectedItem.therapy?.draftId) {
          this.deletedDraftIds.delete(this.selectedItem.therapy.draftId);
        }
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },
    isTherapyStatusSynced(therapy) {
      return therapy.status === TherapyStatusEnum.SYNCED;
    },
    isTherapyStatusInQueue(therapy) {
      return therapy.status === TherapyStatusEnum.IN_QUEUE;
    },
    isTherapyStatusInQueueWithError(therapy) {
      return therapy.status === TherapyStatusEnum.IN_QUEUE_WITH_ERROR;
    },
    isTherapyStatusNotSynced(therapy) {
      return therapy.status === TherapyStatusEnum.NOT_SYNCED;
    },
    isTherapyStatusInProgress(therapy) {
      return therapy.status === TherapyStatusEnum.IN_PROGRESS;
    },
    async exportAllTherapistReports() {
      const protocols = this.syncedProtocols;

      if (protocols.length === 0) {
        this.$toasted.global.defaultError({
          message: "Nenhuma terapia sincronizada disponível para exportar",
        });
        return;
      }

      // Calcula estimativa de tempo (15 segundos por terapia)
      const estimatedSeconds = protocols.length * 15;
      const estimatedMinutes = Math.ceil(estimatedSeconds / 60);
      const timeMessage =
        estimatedMinutes > 1
          ? `aproximadamente ${estimatedMinutes} minutos`
          : `aproximadamente ${estimatedSeconds} segundos`;

      // Mostra modal de confirmação
      const result = await this.$swal({
        title: "Exportar Relatórios do Terapeuta",
        html: `
          <p>Serão gerados <strong>${protocols.length} relatório(s)</strong>.</p>
          <p>⏱️ Tempo estimado: <strong>${timeMessage}</strong></p>
          <p style="margin-top: 15px; color: #666;">A geração pode levar algum tempo. Deseja continuar?</p>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Sim, exportar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#1976D2",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.$store.dispatch("loading/openDialog");

        // Fazer download do ZIP
        const blob = await downloadTherapistBatchPdf(protocols);

        // Criar link para download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `relatorios-terapeuta-${new Date().getTime()}.zip`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$toasted.global.defaultSuccess({
          message: `${protocols.length} relatório(s) do terapeuta exportado(s) com sucesso!`,
        });
      } catch (error) {
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },
    async exportAllPatientReports() {
      const protocols = this.syncedProtocols;

      if (protocols.length === 0) {
        this.$toasted.global.defaultError({
          message: "Nenhuma terapia sincronizada disponível para exportar",
        });
        return;
      }

      // Calcula estimativa de tempo (15 segundos por terapia)
      const estimatedSeconds = protocols.length * 15;
      const estimatedMinutes = Math.ceil(estimatedSeconds / 60);
      const timeMessage =
        estimatedMinutes > 1
          ? `aproximadamente ${estimatedMinutes} minutos`
          : `aproximadamente ${estimatedSeconds} segundos`;

      // Mostra modal de confirmação
      const result = await this.$swal({
        title: "Exportar Relatórios do Paciente",
        html: `
          <p>Serão gerados <strong>${protocols.length} relatório(s)</strong>.</p>
          <p>⏱️ Tempo estimado: <strong>${timeMessage}</strong></p>
          <p style="margin-top: 15px; color: #666;">A geração pode levar algum tempo. Deseja continuar?</p>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Sim, exportar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#1976D2",
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        this.$store.dispatch("loading/openDialog");

        // Fazer download do ZIP
        const blob = await downloadPatientBatchPdf(protocols);

        // Criar link para download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `relatorios-paciente-${new Date().getTime()}.zip`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$toasted.global.defaultSuccess({
          message: `${protocols.length} relatório(s) do paciente exportado(s) com sucesso!`,
        });
      } catch (error) {
        this.$errorHandler(error);
      } finally {
        this.$store.dispatch("loading/closeDialog");
      }
    },

    /**
     * 🔥 Inicia polling para atualizar status de terapias em fila
     */
    startPolling() {
      // Se já existe um timer, não cria outro
      if (this.pollingTimer) {
        return;
      }

      console.log(
        `[Polling] ▶️ Iniciado - verifica a cada ${
          this.pollingInterval / 1000
        }s`
      );

      this.pollingTimer = setInterval(() => {
        this.refreshTherapiesStatus();
      }, this.pollingInterval);
    },

    /**
     * 🔥 Para o polling
     */
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
        console.log("[Polling] ⏹️ Parado");
      }
    },

    /**
     * 🔥 Atualiza status das terapias (sem loading)
     */
    async refreshTherapiesStatus() {
      try {
        if (!navigator.onLine) {
          console.log("[Polling] ⚠️ Offline - pulando atualização");
          return;
        }

        console.log("[Polling] 🔄 Atualizando status das terapias...");

        // Busca terapias atualizadas do backend
        const updatedTherapies = await findByTherapist();

        // Busca drafts da nuvem
        let cloudDrafts = [];
        try {
          cloudDrafts = await listDrafts();

          // 🔥 FIX: Filtra drafts que foram deletados (mas ainda aparecem no backend devido a delay)
          cloudDrafts = cloudDrafts.filter((draft) => {
            const wasDeleted = this.deletedDraftIds.has(draft.id);
            if (wasDeleted) {
              console.log(
                "[Polling] ⏭️ Ignorando draft deletado (ID:",
                draft.id,
                ")"
              );
            }
            return !wasDeleted;
          });
        } catch (error) {
          console.error("[Polling] Erro ao buscar drafts da nuvem:", error);
        }

        // Formata drafts
        const formattedDrafts = cloudDrafts.map((draft) => ({
          patientName:
            draft.patient?.name ||
            draft.patientName ||
            draft.protocol ||
            "Rascunho sem nome",
          email: draft.patient?.mail || draft.patientEmail || "",
          document: draft.patient?.userData?.document || "",
          realizedAt: draft.createdAt,
          status: TherapyStatusEnum.IN_PROGRESS,
          therapy: {
            ...draft,
            status: TherapyStatusEnum.IN_PROGRESS,
            draftId: draft.id,
            protocol: draft.protocol,
            presignedUrl: draft.presignedUrl,
            presignedUrlExpiresAt: draft.presignedUrlExpiresAt,
            s3BackupKey: draft.s3BackupKey,
            step: draft.currentStep,
            isCloudDraft: true,
            patient: {
              id: draft.patient?.id || null,
              name: draft.patient?.name || draft.patientName || "",
              email: draft.patient?.mail || draft.patientEmail || "",
              document: draft.patient?.userData?.document || "",
            },
          },
        }));

        // Combina e remove duplicatas
        const allTherapies = [...formattedDrafts, ...updatedTherapies];
        const uniqueTherapies = new Map();
        allTherapies.forEach((therapy) => {
          const key =
            therapy.therapy?.protocol ||
            therapy.protocol ||
            `local_${therapy.therapy?.localId || Math.random()}`;
          if (!uniqueTherapies.has(key) || therapy.therapy?.isCloudDraft) {
            uniqueTherapies.set(key, therapy);
          }
        });

        this.therapies = Array.from(uniqueTherapies.values());
        this.filterTherapies();

        // 🔥 Limpeza do cache: remove IDs que definitivamente não existem mais no backend
        const currentDraftIds = new Set(cloudDrafts.map((d) => d.id));
        const idsToRemove = [];
        this.deletedDraftIds.forEach((deletedId) => {
          if (!currentDraftIds.has(deletedId)) {
            idsToRemove.push(deletedId);
          }
        });
        idsToRemove.forEach((id) => {
          this.deletedDraftIds.delete(id);
          console.log(
            "[Polling] 🧹 ID removido do cache (confirmado no backend):",
            id
          );
        });

        // Verifica se alguma terapia mudou de status
        const stillInQueue = this.therapies.some(
          (t) =>
            t.status === TherapyStatusEnum.IN_QUEUE ||
            t.status === TherapyStatusEnum.IN_QUEUE_WITH_ERROR
        );

        if (!stillInQueue) {
          console.log("[Polling] ✅ Todas as terapias foram processadas");
          this.$toasted.success("Terapia(s) processada(s) com sucesso!", {
            duration: 4000,
          });
        } else {
          console.log("[Polling] ⏳ Ainda há terapias em processamento");
        }
      } catch (error) {
        console.error("[Polling] Erro ao atualizar status:", error);
        // Não exibe erro ao usuário para não ser intrusivo
      }
    },
  },
};
</script>

<style scoped>
/* 🔥 Animação de rotação para o ícone de sincronização */
.rotating-icon {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
